import type {
    DateRange,
    DateRangeChange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import { ALL_TIME_SENTINEL_FROM } from '@/components/molecules/DateRangePicker/DateRangePresets';
import { type AuditLogFilters, dateRangeToFilterBounds } from '@/domain/auditLog';
import { toDateString } from '@/utils/date';

function today(): string {
    return toDateString(new Date());
}

/**
 * The days the picker shows for the time filter, read from the filters every render, so a
 * pivot or "Clear all" that drops the bounds also resets the picker. The picker has whole
 * days only: the `to` bound is exclusive, so its day is the one before the instant, and an
 * open side shows as the picker's own "all" start or today. A bound with a time of day
 * shows its whole day here, and the chip row gives the exact times.
 */
export function pickerRange(filters: AuditLogFilters): DateRange {
    const { from, to } = filters;
    if (from === undefined && to === undefined) return {};
    return {
        dateFrom: from === undefined ? ALL_TIME_SENTINEL_FROM : toDateString(from),
        dateTo: toDateString(to === undefined ? new Date() : new Date(to.getTime() - 1)),
    };
}

/**
 * The filters with the picked days as local-midnight bounds, the end exclusive. The picker
 * sends its 1970 sentinel both for the "all" preset, which ends today, and as the start it
 * shows for a range with only an end, so only a sentinel ending today means "all"; with
 * any other end the start stays open and the end is kept. This endpoint takes an open
 * range, so the sentinel is never sent. The picker has whole days only, so on a Confirm
 * of the custom days a side whose day was left as shown keeps its exact time: confirming
 * must not widen a window set to the minute in the panel or the URL. A preset or Reset
 * always sets whole days, even where its day matches the one shown.
 */
export function withPickedRange(
    filters: AuditLogFilters,
    range: DateRange,
    change: DateRangeChange
): AuditLogFilters {
    const openStart = range.dateFrom === ALL_TIME_SENTINEL_FROM;
    const everything = openStart && (range.dateTo === undefined || range.dateTo === today());
    const next: AuditLogFilters = { ...filters };
    delete next.from;
    delete next.to;
    if (everything) return next;

    const bounds = dateRangeToFilterBounds(openStart ? undefined : range.dateFrom, range.dateTo);
    const shown: DateRange = change.source === 'custom' ? pickerRange(filters) : {};
    const from =
        filters.from !== undefined &&
        range.dateFrom !== undefined &&
        range.dateFrom === shown.dateFrom
            ? filters.from
            : bounds.from;
    const to =
        filters.to !== undefined && range.dateTo !== undefined && range.dateTo === shown.dateTo
            ? filters.to
            : bounds.to;
    if (from !== undefined) next.from = from;
    if (to !== undefined) next.to = to;
    return next;
}
